import React, {lazy, useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {getReloadApOverlay} from 'modules/overlay/selectors'
import {showOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {toggle} from 'opticks'

import {BottomSheet} from '@daedalus/atlas/BottomSheet'
import {Button} from '@daedalus/atlas/Button'
import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {PopUp} from '@daedalus/atlas/PopUp'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@daedalus/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'

const ReloadPageContent = lazy(
  () =>
    import(
      /* webpackChunkName: "ReloadAPContent" */
      './ReloadAPContent'
    )
)

const POPUP_WIDTH = 522

const TIME_TO_RELOAD = 25 * 60 * 1000 // 25 mins

export const ReloadAP = () => {
  const {isMobile} = useDeviceLayout()

  const dispatch = useDispatch()
  const isOpen = Boolean(useSelector(getReloadApOverlay))

  const setReloadTimeout = useCallback(
    () =>
      setTimeout(() => {
        if (toggle('8ae0388a-ap-offers-refresh', false, true)) {
          dispatch(
            showOverlay({
              type: OverlayType.ReloadAP
            })
          )
        }

        dispatch(
          trackEvent({
            category: Category.System,
            entity: Entity.PriceRefresh,
            action: Action.Displayed,
            page: Page.Accommodation
          })
        )
      }, TIME_TO_RELOAD),
    [dispatch]
  )

  const handleReload = (
    component: 'primaryRefreshButton' | 'crossRefreshButton'
  ) => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.PriceRefresh,
        action: Action.Clicked,
        page: Page.Accommodation,
        payload: {
          component
        }
      })
    )
    setTimeout(() => window.location.reload(), 200)
  }

  useEffect(() => {
    const timeout = setReloadTimeout()

    return () => clearTimeout(timeout)
  }, [setReloadTimeout])

  if (toggle('8ae0388a-ap-offers-refresh', true, false)) {
    return null
  }

  return isMobile ? (
    <BottomSheet
      withDragHandler
      isOpen={isOpen}
      onClose={() => handleReload('crossRefreshButton')}
      prefersReducedMotion
      titleBarProps={{
        centerContent: (
          <FormattedMessageWrapper
            id="seeBestDealsAvailableNow"
            defaultMessage="See best deals available now"
          />
        )
      }}
      bottomBarContent={
        <Button fullWidth onClick={() => handleReload('primaryRefreshButton')}>
          <FormattedMessageWrapper
            id="seeLatestDeals"
            defaultMessage="See latest deals"
          />
        </Button>
      }
    >
      <ReloadPageContent />
    </BottomSheet>
  ) : (
    <PopUp
      width={POPUP_WIDTH}
      isOpen={isOpen}
      closeAction={() => handleReload('crossRefreshButton')}
      titleBarProps={{
        centerContent: (
          <FormattedMessageWrapper
            id="seeBestDealsAvailableNow"
            defaultMessage="See best deals available now"
          />
        ),
        hasDivider: true
      }}
      footer={
        <Button fullWidth onClick={() => handleReload('primaryRefreshButton')}>
          <FormattedMessageWrapper
            id="seeLatestDeals"
            defaultMessage="See latest deals"
          />
        </Button>
      }
    >
      <ReloadPageContent />
    </PopUp>
  )
}
